import { useEffect, useState } from "react";
import { Bell, Phone, CalendarCheck, MessageSquare, Sparkles, CreditCard, Megaphone, CheckCheck, Settings2, UserPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import NotificationSettings from "@/components/dashboard/NotificationSettings";

const CAT_ICON = {
  phone: Phone, booking: CalendarCheck, message: MessageSquare, ai: Sparkles,
  payment: CreditCard, lead: UserPlus, system: Megaphone,
};
const CAT_COLOR = {
  phone: "bg-blue-50 text-blue-600", booking: "bg-emerald-50 text-emerald-600",
  message: "bg-violet-50 text-violet-600", ai: "bg-amber-50 text-amber-600",
  payment: "bg-teal-50 text-teal-600", lead: "bg-pink-50 text-pink-600", system: "bg-slate-100 text-slate-600",
};

function timeAgo(iso) {
  const d = new Date(iso), diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return d.toLocaleDateString();
}

export default function NotificationCentre() {
  const [tab, setTab] = useState("activity");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => api.get("/notifications", { params: { limit: 100 } }).then((r) => { setItems(r.data.notifications || []); setLoading(false); }).catch(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const markAll = async () => { await api.post("/notifications/read-all"); load(); window.dispatchEvent(new Event("notif-refresh")); };
  const markRead = async (id) => { await api.post(`/notifications/${id}/read`); load(); window.dispatchEvent(new Event("notif-refresh")); };

  const unread = items.filter((i) => !i.read).length;

  return (
    <div data-testid="notification-centre">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight flex items-center gap-2"><Bell className="h-6 w-6 text-blue-600" /> Notifications</h1>
          <p className="text-slate-500 mt-1">Everything happening across your account, in one place.</p>
        </div>
        {tab === "activity" && unread > 0 && (
          <Button data-testid="mark-all-read" onClick={markAll} variant="outline" size="sm" className="gap-2"><CheckCheck className="h-4 w-4" /> Mark all read</Button>
        )}
      </div>

      <div className="mt-4 flex gap-2 border-b border-slate-200">
        <button data-testid="tab-activity" onClick={() => setTab("activity")} className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px ${tab === "activity" ? "border-blue-600 text-blue-600" : "border-transparent text-slate-500"}`}>Activity {unread > 0 && <span className="ml-1 rounded-full bg-blue-600 text-white text-xs px-1.5">{unread}</span>}</button>
        <button data-testid="tab-preferences" onClick={() => setTab("preferences")} className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px flex items-center gap-1 ${tab === "preferences" ? "border-blue-600 text-blue-600" : "border-transparent text-slate-500"}`}><Settings2 className="h-3.5 w-3.5" /> Preferences</button>
      </div>

      {tab === "preferences" ? (
        <div className="mt-6"><NotificationSettings /></div>
      ) : (
        <div className="mt-4 space-y-2">
          {loading && <p className="text-slate-400 py-10 text-center">Loading…</p>}
          {!loading && items.length === 0 && (
            <div className="py-16 text-center text-slate-400" data-testid="notif-empty"><Bell className="h-10 w-10 mx-auto mb-2 opacity-40" /><p>No notifications yet.</p></div>
          )}
          {items.map((n) => {
            const Icon = CAT_ICON[n.category] || Bell;
            return (
              <div key={n.id} data-testid="notif-item" onClick={() => !n.read && markRead(n.id)}
                className={`flex gap-3 rounded-xl border p-4 cursor-pointer transition-colors ${n.read ? "border-slate-100 bg-white" : "border-blue-200 bg-blue-50/40 hover:bg-blue-50"}`}>
                <div className={`h-9 w-9 rounded-lg flex items-center justify-center shrink-0 ${CAT_COLOR[n.category] || "bg-slate-100 text-slate-600"}`}><Icon className="h-4 w-4" /></div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <p className="font-semibold text-sm text-slate-900 truncate">{n.title}</p>
                    <span className="text-xs text-slate-400 shrink-0">{timeAgo(n.created_at)}</span>
                  </div>
                  {n.body && <p className="text-sm text-slate-500 mt-0.5">{n.body}</p>}
                </div>
                {!n.read && <span className="h-2 w-2 rounded-full bg-blue-600 shrink-0 mt-2" />}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
